import React, { useEffect, useState, useRef } from "react";
import { Trash2, Phone, User, CalendarDays, Clock, Search, RefreshCw } from "lucide-react";
import { supabase } from "../../config/supabase";
import { useRealtimeSessions } from "../../hooks/useRealtime";
import "./adminSession.css";

const AdminSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  // Real-time: Fetch immediately when new session arrives
  useRealtimeSessions((data) => {
    console.log('New session received:', data);
    fetchSessions(); // Refresh the list
  });

  useEffect(() => {
    fetchSessions();
    intervalRef.current = setInterval(fetchSessions, 10000); // 10s backup polling
    return () => clearInterval(intervalRef.current);
  }, []);

  // Fetch directly from Supabase (works even if backend is down)
  const fetchSessions = async () => {
    try {
      setError(null);
      const { data, error } = await supabase
        .from('sessions')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      console.log('Sessions fetched from Supabase:', data);
      setSessions(data || []);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (err) {
      console.error("Failed to fetch sessions:", err);
      setError("Failed to load sessions. Please try again.");
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this session?"
    );
    if (!confirmDelete) return;

    try {
      const { error } = await supabase
        .from('sessions')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      setSessions((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error("Failed to delete session:", err);
      alert("Failed to delete session. Please try again.");
    }
  };

  const filteredSessions = (sessions || []).filter(session =>
    session?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session?.topic?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session?.phone?.includes(searchTerm)
  );

  if (loading) {
    return <div className="loading">Loading sessions...</div>;
  }

  return (
    <div className="admin-session-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <h2 style={{ margin: 0 }}>Student Session Requests</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {lastUpdated && (
            <span style={{ fontSize: '12px', color: '#888' }}>
              🟢 Live · Last updated: {lastUpdated}
            </span>
          )}
          <button
            onClick={fetchSessions}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {error && <div style={{ background: '#fff3cd', color: '#856404', padding: '10px 14px', borderRadius: '6px', marginBottom: '12px' }}>{error}</div>}

      {/* Search Bar */}
      <div className="search-bar" style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', border: '1px solid #ddd', borderRadius: '8px', padding: '8px 12px', background: '#f9f9f9' }}>
        <Search size={16} />
        <input
          type="text"
          placeholder="Search by name, topic, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ border: 'none', outline: 'none', marginLeft: '8px', flex: 1, fontSize: '14px' }}
        />
      </div>

      {filteredSessions.length === 0 ? (
        <p className="empty">No session requests found</p>
      ) : (
        <div className="session-card-grid">
          {filteredSessions.map((session) => (
            <div className="session-card" key={session.id}>
              {/* Header */}
              <div className="session-card-header">
                <h3>
                  <User size={16} /> {session.full_name}
                </h3>
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(session.id)}
                  title="Delete session"
                >
                  <Trash2 size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="session-card-body">
                <p>
                  <Phone size={14} /> {session.phone}
                </p>
                <p className="topic">{session.topic}</p>
                <p className="meta">
                  <CalendarDays size={14} /> {session.session_date || "—"}
                </p>
                <p className="meta">
                  <Clock size={14} /> {session.created_at}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSessions;